'use client';

import CreditBalance from '@/components/CreditBalance';
import WorkspaceSwitcher from '@/components/Molecules/WorkspaceSwitcher';
import { useWorkspace } from '@/contexts/WorkspaceProvider';
import { useWorkspaceDetection } from '@/hooks/useWorkspaceDetection';
import { useNotifications } from '@/contexts/NotificationProvider';
import { countUnseenActiveNotifications } from '@/utils/notificationHelpers';
import { useProtectedLayoutContext } from '../context/ProtectedLayoutProvider';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Image from 'next/image';
import Link from 'next/link';
import { FC, useState, useMemo } from 'react';
import AnnouncementsBell from '@/components/announcements/AnnouncementsBell';
import HamburgerButton from '../HamburgerButton';
import Sidebar from '../Sidebar';

type IMobileHeaderProps = object;

const MobileHeader: FC<IMobileHeaderProps> = () => {
  const [isShowMenu, setIsShowMenu] = useState(false);
  const workspaceContext = useWorkspace();
  const { shouldShowWorkspaceSwitcher } = useWorkspaceDetection();
  const { setIsNotificationsDrawerOpen } = useProtectedLayoutContext();
  const { notifications, notificationsSeenAt } = useNotifications();

  // Badge counts active notifications not seen since the drawer was last opened
  // (shared seenAt) so it clears on open and doesn't stick on stale items.
  // Marking-seen happens in ProtectedLayout's shared drawer-open effect.
  const activeNotificationCount = useMemo(() => {
    return countUnseenActiveNotifications(notifications, notificationsSeenAt);
  }, [notifications, notificationsSeenAt]);

  return (
    <header
      className='fixed top-0 left-0 right-0 md:hidden z-20 bg-white border-b border-gray-200 box-border flex items-center min-h-[var(--mobile-header-bar)] pt-[env(safe-area-inset-top,0px)]'
    >
      <div className='flex justify-between items-center px-4 w-full h-[var(--mobile-header-bar)]'>
        <div className='flex items-center gap-3'>
          <Link href='/'>
            <Image src='/images/logo/mb_small.png' width={48} height={48} alt='marketbetter' className='w-10 h-10 object-contain' />
          </Link>
          {/* Workspace switcher - Only show when user has multiple workspaces */}
          {workspaceContext && shouldShowWorkspaceSwitcher && (
            <div className='flex-1'>
              <WorkspaceSwitcher className='h-10 min-w-[40px]' align='start' />
            </div>
          )}
        </div>
        <div className='flex items-center gap-2'>
          <CreditBalance
            compact
            className='text-xs px-2 py-1.5 bg-gray-50 rounded h-10 flex items-center'
          />
          
          {/* What's-new announcements bell */}
          <AnnouncementsBell className="h-10 w-10 border-0 shadow-none" />

          {/* Notifications Bell */}
          <Button
            variant="ghost"
            size="icon"
            className="relative h-10 w-10 flex items-center justify-center text-slate-600"
            onClick={() => setIsNotificationsDrawerOpen(true)}
          >
            <Bell className="h-5 w-5" />
            {activeNotificationCount > 0 && (
              <span className="absolute top-1.5 right-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-blue-500 px-1 text-[10px] font-medium text-white">
                {activeNotificationCount > 99 ? '99+' : activeNotificationCount}
              </span>
            )}
          </Button>

          <HamburgerButton onClick={() => setIsShowMenu(true)} />
        </div>
      </div>

      {isShowMenu && <Sidebar onCloseSidebar={() => setIsShowMenu(false)} />}
    </header>
  );
};

export default MobileHeader;
